import {toSecureCardNumber} from '@yandex-delivery/core/formatters'
import {allPass, anyPass, equals, flow, formatDate, get, ifElse, toNumber} from '@yandex-delivery/utils'

import {
  type KnownCardSystem,
  type PaymentCard,
  type PaymentContract,
  PaymentContractErrorCode,
  PaymentContractStatus,
} from './types'

export * from './types'

const formatToMonthName = formatDate('MMMM')

export function formatCardNumber(payment: PaymentCard): string {
  return toSecureCardNumber(payment.number, payment.system)
}

export function hasPenalty(payment?: PaymentContract): boolean {
  return payment?.penalty ? payment.penalty > 0 : false
}

export const hasOutstandingPayment: (payment?: PaymentContract) => boolean = flow(
  get('lastDocumentPaymentInfo.status'),
  anyPass([equals(PaymentContractStatus.NotPaid), equals(PaymentContractStatus.PartiallyPaid)]),
)

export const isPending: (payment?: PaymentContract) => boolean = flow(
  get('errorCode'),
  anyPass([
    equals(PaymentContractErrorCode.ContractIsPending),
    equals(PaymentContractErrorCode.BalanceNotFoundForPrepaid),
  ]),
)

export const isPrepaid: (payment?: PaymentContract) => boolean = flow(get('paymentType'), equals('prepaid'))

export const getBalance: (payment?: PaymentContract) => number = flow(get('balance'), toNumber)

export const getPostpaidAmount: (payment?: PaymentContract) => number = flow(
  ifElse(hasOutstandingPayment, get('debtAmount'), get('balance')),
  toNumber,
)

export const getAmount: (payment?: PaymentContract) => number = ifElse(isPrepaid, getBalance, getPostpaidAmount)

export const hasError: (payment?: PaymentContract) => boolean = anyPass([
  flow(get('errorCode'), Boolean),
  hasOutstandingPayment,
])

export const getMonthName: (payment?: PaymentContract) => string | undefined = flow(
  get('lastDocumentPaymentInfo.month'),
  function _toMonthName(maybeMonth?: string) {
    return maybeMonth ? formatToMonthName(maybeMonth) : undefined
  },
)

export function checkBalanceBelowThreshold(payment?: PaymentContract): boolean {
  return payment ? payment.balance < payment.threshold : false
}

export function checkBalanceAvailable(payment?: PaymentContract): boolean {
  return !hasOutstandingPayment(payment)
}

export function checkOutstandingPaymentWithoutPenalty(payment?: PaymentContract, hasBalanceAccess = false): boolean {
  return hasBalanceAccess && hasOutstandingPayment(payment) && !hasPenalty(payment)
}

export const checkOutstandingPaymentWithPenalty: (payment?: PaymentContract) => boolean = allPass([
  hasOutstandingPayment,
  hasPenalty,
])

export const isKnownCardSystem = (card: string): card is KnownCardSystem => {
  return ['visa', 'mastercard', 'mir', 'maestro'].includes(card)
}
